import React, { FC } from 'react';
import { TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '@ui/Report';
import { REPORT_PAGE_TITLE } from './editable-page-header.constants';
import { useReportEditableFieldDisplay } from './use-report-editable-field-display.hook';

type ReportPageTitleFieldProps = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  /** Max width in cqw (pass {@link figmaPxToWidthPercent} for Figma px). */
  maxWidth?: number;
  /** Hug the typed title so a suffix can sit immediately after it. */
  fitContent?: boolean;
};

/**
 * Inline-editable report page title — Figma node 4767:41120.
 * Shared by proposal dashboard pages and other full-bleed report headers.
 */
export const ReportPageTitleField: FC<ReportPageTitleFieldProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  maxWidth,
  fitContent = false,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize, maxWidth, fitContent });
  const { displayValue, activePlaceholder, bindFocusHandlers } =
    useReportEditableFieldDisplay(value, placeholder);

  return (
    <TextField
      fullWidth={!fitContent}
      variant="standard"
      autoComplete="off"
      placeholder={activePlaceholder}
      value={displayValue}
      onChange={onChange}
      className={classes.root}
      slotProps={{
        htmlInput: { autoComplete: 'off', className: classes.htmlInput },
        input: {
          disableUnderline: true,
          className: classes.input,
          ...bindFocusHandlers(onBlur),
        },
      }}
    />
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
  maxWidth?: number;
  fitContent: boolean;
}>()((_theme, { getScalingSize, maxWidth, fitContent }) => ({
  root: {
    display: fitContent ? 'inline-flex' : undefined,
    verticalAlign: fitContent ? 'baseline' : undefined,
    width: fitContent ? 'max-content' : '100%',
    maxWidth: maxWidth !== undefined ? getScalingSize(maxWidth) : '100%',
    minWidth: 0,
  },
  // MUI's single-line input carries a fixed-px default padding ('4px 0 5px')
  // on the native <input>. That px offset ignores the report's cqw scaling, so
  // it dominates at thumbnail scale and shifts the title down. Zero it — the
  // scaled font/line-height on the root already sets the box height.
  htmlInput: {
    padding: 0,
    height: 'auto',
    lineHeight: 'inherit',
    ...(fitContent && { fieldSizing: 'content' }),
  },
  input: {
    padding: 0,
    width: fitContent ? 'auto' : '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    fontFamily: REPORT_PAGE_TITLE.fontFamily,
    fontSize: getScalingSize(REPORT_PAGE_TITLE.fontSize),
    fontWeight: REPORT_PAGE_TITLE.fontWeight,
    lineHeight: REPORT_PAGE_TITLE.lineHeight,
    color: REPORT_PAGE_TITLE.color,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&::placeholder': {
      color: REPORT_PAGE_TITLE.color,
      opacity: 0.5,
    },
  },
}));
